import { VaultDepositor } from '@velocity-exchange/vaults-sdk';
import { Vault } from '@velocity-exchange/vaults-sdk';
import { SerializedVaultDepositorRecord } from 'src/db/schema/vault-depositor-records';
import { MarketId } from '@velocity-exchange/common';

import { BigNum } from '@velocity-exchange/sdk';
import { useMemo } from 'react';
import useGetOraclePriceForMarket from '../useGetOraclePriceForMarket';
import { useSpotMarketsLookup } from '../spotMarkets/useSpotMarketsLookup';
import {
	calcVaultDepositorBasePnl,
	calcVaultDepositorNotionalPnl,
} from 'src/utils/vault/vaultDepositor';
import { VaultStats } from 'src/@types/vaults';
import { useVaultDepositorHistories } from 'src/hooks/query/vaults/base/useVaultDepositorHistories';
import { useVaultStats } from 'src/hooks/query/vaults/derived/useVaultStats';

const DEFAULT_VAULT_DEPOSITOR_HISTORY: SerializedVaultDepositorRecord[] = [];

export interface VaultDepositorEarnings {
	basePnl: BigNum;
	notionalPnl: BigNum;
}

export const useVaultDepositorEarnings = (
	vaultPubkey: string,
	vaultDepositor: VaultDepositor,
	vault: Vault
): VaultDepositorEarnings => {
	const { data: historiesLookup } = useVaultDepositorHistories();
	const vaultDepositorHistory =
		historiesLookup?.[vaultPubkey] ?? DEFAULT_VAULT_DEPOSITOR_HISTORY;
	const { data: vaultStats } = useVaultStats(vaultPubkey);
	const getOraclePriceForMarket = useGetOraclePriceForMarket();
	const spotMarkets = useSpotMarketsLookup();

	const spotMarketConfig = spotMarkets[vault.spotMarketIndex];

	const basePnl = useMemo(() => {
		return calcVaultDepositorBasePnl({
			vaultDepositor,
			vault,
			vaultAssetPrecision: spotMarketConfig.precisionExp,
			vaultStats: vaultStats as VaultStats,
		});
	}, [vaultDepositor, vault, spotMarketConfig.precisionExp, vaultStats]);

	const notionalPnl = useMemo(() => {
		const oraclePrice = getOraclePriceForMarket(
			MarketId.createSpotMarket(vault.spotMarketIndex)
		);

		return calcVaultDepositorNotionalPnl({
			vaultDepositor,
			vault,
			oraclePrice: oraclePrice as BigNum,
			vaultAssetPrecision: spotMarketConfig.precisionExp,
			vaultStats: vaultStats as VaultStats,
			vaultDepositorHistory,
		});
	}, [
		vaultDepositor,
		vault,
		spotMarketConfig.precisionExp,
		vaultStats,
		vaultDepositorHistory,
	]);

	const vaultDepositorEarnings = useMemo(() => {
		return {
			basePnl,
			notionalPnl,
		};
	}, [basePnl, notionalPnl]);

	return vaultDepositorEarnings;
};
